import React, { useMemo } from 'react'
import { useParams, Outlet } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { useNavigate } from 'router'
import { useTranslation } from 'react-i18next'
import { useTheme, useMediaQuery, Box } from '@mui/material'

import { OPERATION_FIND_CONTACT, OPERATION_CAN_EDIT_CONTACT } from '../../operations-gql/queries'
import { useUser } from 'store'
import { SideContent } from './PreviewStyles'

import ActionEdit from './components/ActionEdit'
import Header from './Header'
import Body from './Body'
import CardPreview from 'packages/ui/CardPreview'
import SlideInModal from 'components/Modal/SlideInModal'
import Loading from 'components/global/Loading'

const Card = () => {
  const { t } = useTranslation()
  const { sfId } = useParams()
  const navigate = useNavigate()
  const [user] = useUser()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  
  if (!sfId) return null;
  const canEditQuery = useQuery(OPERATION_CAN_EDIT_CONTACT, {
    variables: {
      sf_id: sfId,
    },
    skip: !isNaN(sfId)
  })
  const { loading, data } = useQuery(OPERATION_FIND_CONTACT, {
    variables: {
      sf_id: sfId,
      with_accounts: false
    },
    fetchPolicy: 'no-cache'
  })

  const canEdit = useMemo(() => {
    if (isNaN(sfId) && !canEditQuery.data?.canEdit) {
      return false
    }
    return user.admin || (user.roles.admin_roles || user.roles.admin_contact) && !user.roles.readonly_contact
  }, [user, canEditQuery])

  const handleClose = () => {
    navigate({ pathname: '..' })
  }

  if (isLargeScreen) {
    return (
      <SideContent style={{ width: 420 }}>
        <CardPreview
          title={t('ui_elements.card_title')}
          loading={loading}
          onClose={handleClose}
          components={
            !loading && canEdit
              ? {
                  ActionButtons: () => <ActionEdit contact={data.contact} />
                }
              : {}
          }>
          {data && (
            <>
              <Header contact={data.contact} />
              <Body contact={data.contact} />
            </>
          )}
        </CardPreview>
        <Outlet />
      </SideContent>
    )
  }
  
  return (
    <>
      <SlideInModal
        title={t('ui_elements.card_title')}
        onClose={handleClose}
        width={isSmallScreen ? '100%' : '450px'}
        fullScreen={isSmallScreen}
        actionButton={!loading && canEdit ? <ActionEdit contact={data.contact} /> : null}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Loading />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {
              Boolean(data) && (
                <>
                  <Header contact={data.contact} />
                  <Body contact={data.contact} />
                </>
              )
            }
          </Box>
        )}
      </SlideInModal>
      <Outlet />
    </>
  )
}

export default Card
